/**
 * faqChatbotEngine.js — Re Leve IA (Etapa 11)
 *
 * Motor local de FAQ: responde dúvidas frequentes com regras determinísticas
 * e guardrails. Usado como fallback quando a API/LLM não está disponível.
 *
 * REGRA: nunca prometer emagrecimento, nunca prescrever dieta, nunca inventar dados.
 */

const FAQ_GUARDRAILS = [
  { id: 'FAQ-G01', re: /emagrecer|perder peso|secar|vou emagrecer/i, response: 'Posso te ajudar a organizar sua alimentação, mas resultados de emagrecimento dependem de fatores individuais. Para orientação personalizada, recomendamos um nutricionista.' },
  { id: 'FAQ-G02', re: /dieta|plano alimentar individual|prescri/i, response: 'A Re Leve não prescreve dieta. Nosso acompanhamento calórico é informativo. Para um plano alimentar individualizado, indicamos um nutricionista.' },
  { id: 'FAQ-G03', re: /substitui nutricion|voce eh nutri|você é nutri/i, response: 'Não substituo nutricionista. Posso te ajudar com informações sobre os kits e funcionamento da Re Leve.' },
  { id: 'FAQ-G04', re: /numero do cartao|cvv|senha do cartao|pix de verdade/i, response: 'Por segurança, dados de cartão não passam por aqui. Quando o pagamento real estiver ativo, será via Stripe Checkout — página segura do Stripe.' },
  { id: 'FAQ-G05', re: /alergia confirm|alergia grave/i, response: 'Alergias confirmadas precisam ser tratadas pelo atendimento humano, com confirmação da nutricionista. Posso encaminhar?' },
];

const FAQ_QUICK_QUESTIONS = [
  { id: 'q_kits',       label: 'Como funcionam os kits da semana?',    message: 'Como funcionam os kits da semana?' },
  { id: 'q_calorias',   label: 'Posso acompanhar calorias?',           message: 'Posso acompanhar calorias?' },
  { id: 'q_pagamento',  label: 'O pagamento é seguro?',                message: 'O pagamento é seguro?' },
  { id: 'q_entrega',    label: 'Como funciona a entrega?',             message: 'Como funciona a entrega?' },
  { id: 'q_empresas',   label: 'Vocês montam pedidos para empresas?',  message: 'Vocês montam pedidos para empresas?' },
  { id: 'q_restricao',  label: 'Posso informar restrições alimentares?', message: 'Posso informar restrições alimentares?' },
  { id: 'q_nutri',      label: 'O bot substitui nutricionista?',       message: 'O bot substitui nutricionista?' },
  { id: 'q_humano',     label: 'Como falar com atendimento humano?',   message: 'Como falar com atendimento humano?' },
];

export function getFAQQuickQuestions() {
  return FAQ_QUICK_QUESTIONS;
}

export function applyFAQGuardrails(message) {
  if (!message) return null;
  for (const g of FAQ_GUARDRAILS) {
    if (g.re.test(message)) return g;
  }
  return null;
}

export function classifyFAQIntent(message) {
  const m = (message || '').toLowerCase();
  if (/kit|semana|monta|dia/.test(m)) return 'faq_kits';
  if (/caloria|calórico|calorico|kcal/.test(m)) return 'faq_calorias';
  if (/pagamento|pagar|preço|preco|stripe|cobranca|cobrança/.test(m)) return 'faq_pagamento';
  if (/entrega|entregar|frete|prazo/.test(m)) return 'faq_entrega';
  if (/empresa|escritório|escritorio|coletivo|equipe/.test(m)) return 'faq_empresas';
  if (/restricao|restrição|alergia|intoler|lactose|gluten|glúten|vegano|vegetariano/.test(m)) return 'faq_restricoes';
  if (/nutricion|profissional/.test(m)) return 'faq_nutricionista';
  if (/humano|atend|whatsapp|falar com/.test(m)) return 'faq_humano';
  return 'faq_general';
}

function intentResponse(intent) {
  switch (intent) {
    case 'faq_kits':
      return {
        text: 'A Re Leve tem kits para o dia (Dia Leve, Dia Controle, Dia Performance) e para a semana (Semana Econômica, Semana Útil, Semana Completa, Família, Escritório). Você escolhe um e o bot ajuda a montar. Quer ver os destaques?',
        cta: 'Ver kits',
        needsHuman: false,
      };
    case 'faq_calorias':
      return {
        text: 'Sim! O acompanhamento calórico é informativo, não prescritivo. Para usar, você precisa autorizar (consentimento explícito). Os dados ficam no seu próprio navegador.',
        cta: 'Ativar acompanhamento',
        needsHuman: false,
      };
    case 'faq_pagamento':
      return {
        text: 'No protótipo atual, o pagamento é simulado para fins acadêmicos. A arquitetura é Stripe-ready: em produção, o cartão nunca passa pelo nosso site — vai direto para a página segura do Stripe.',
        cta: null,
        needsHuman: false,
      };
    case 'faq_entrega':
      return {
        text: 'A Re Leve entrega em Recife. Para horários e taxas exatas, confirmo com a equipe pelo WhatsApp.',
        cta: 'Falar com equipe',
        needsHuman: true,
      };
    case 'faq_empresas':
      return {
        text: 'Sim, atendemos empresas. Temos kits coletivos como Escritório Saudável e Família Organizada. Para condições específicas, encaminho para nossa equipe comercial.',
        cta: 'Falar com comercial',
        needsHuman: true,
      };
    case 'faq_restricoes':
      return {
        text: 'Você pode registrar preferências (sem lactose, sem glúten, low carb, vegetariano). Para alergias confirmadas, encaminho para a nutricionista da equipe — segurança em primeiro lugar.',
        cta: null,
        needsHuman: false,
      };
    case 'faq_nutricionista':
      return {
        text: 'Não substituo nutricionista. O acompanhamento aqui é informativo. Se você precisa de orientação individual, posso encaminhar para um profissional da equipe.',
        cta: 'Falar com equipe',
        needsHuman: true,
      };
    case 'faq_humano':
      return {
        text: 'Claro! Posso te encaminhar para nossa equipe pelo WhatsApp. Posso preparar um resumo da nossa conversa?',
        cta: 'Falar no WhatsApp',
        needsHuman: true,
      };
    default:
      return {
        text: 'Posso responder dúvidas sobre kits, acompanhamento calórico, pagamento, entrega, restrições alimentares ou encaminhar para o atendimento humano. O que você gostaria de saber?',
        cta: null,
        needsHuman: false,
      };
  }
}

export function getFAQFallbackResponse(message) {
  return {
    text: 'Não tenho certeza dessa resposta. Posso encaminhar para o atendimento humano, que confirma com segurança.',
    intent: 'faq_unknown',
    cta: 'Falar com equipe',
    needsHuman: true,
    guardrails: [],
    source: 'faq_local_fallback',
  };
}

export function generateFAQResponse(message, context = {}) {
  if (!message || !message.trim()) {
    return {
      text: 'Pode escrever sua dúvida ou escolher uma das perguntas frequentes ao lado.',
      intent: 'faq_idle',
      cta: null,
      needsHuman: false,
      guardrails: [],
      source: 'faq_local',
    };
  }

  // Guardrails primeiro
  const guardrail = applyFAQGuardrails(message);
  if (guardrail) {
    return {
      text: guardrail.response,
      intent: `guardrail_${guardrail.id.toLowerCase()}`,
      cta: 'Falar com equipe',
      needsHuman: ['FAQ-G05'].includes(guardrail.id),
      guardrails: [guardrail.id],
      source: 'faq_local_guardrail',
    };
  }

  const intent = classifyFAQIntent(message);
  const base = intentResponse(intent);

  return {
    text: base.text,
    intent,
    cta: base.cta,
    needsHuman: base.needsHuman,
    guardrails: [],
    source: 'faq_local',
  };
}
